import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Camera, Scissors } from "lucide-react";
import { LipstickIcon, CombIcon, SparkleIcon } from "../components/BrandIcons";
import { useContent } from "../context/ContentContext";
import ServiceDetailModal from "../components/ServiceDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Heart, Sparkles, Camera, Scissors };

export default function Services() {
  const [services, setServices] = useState([]);
  const [detailKey, setDetailKey] = useState(null);
  const { content, servicePricing } = useContent();
  const detailByGroup = (servicePricing || []).reduce((acc, g) => { acc[g.id] = g; return acc; }, {});
  const sp = content.services_page || {};
  useEffect(() => { axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="services-page">
      <section className="grain-coral border-b-2 border-ink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10">
          <p className="eyebrow text-ink text-xs">{sp.eyebrow || "Services"}</p>
          <h1 className="heading mt-3 text-4xl md:text-6xl text-ink">{sp.headline || "What I do, beautifully."}</h1>
          <p className="mt-5 max-w-2xl text-lg text-ink/90">{sp.intro || "From your wedding morning to a 6am editorial call time — full-service hair & makeup with one calm, well-prepped artist."}</p>
        </div>
      </section>

      <section className="bg-cream border-b-2 border-ink">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10 space-y-10">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const detail = detailByGroup[s.id];
            const useSparkle = (s.icon || "").toLowerCase().includes("spark") || (s.title || "").toLowerCase().includes("wedding") || (s.title || "").toLowerCase().includes("event");
            return (
              <article key={s.id} className="grid gap-6 md:grid-cols-12 items-stretch" data-testid={`service-row-${s.id}`}>
                <div className={`md:col-span-7 card-brut grain-pink p-8 relative overflow-hidden ${i % 2 ? 'md:order-2' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 grid place-items-center border-2 border-ink bg-cream">
                      {useSparkle ? <SparkleIcon className="h-6 w-6 text-ink" /> : <Icon className="h-6 w-6 text-ink" />}
                    </div>
                    <p className="eyebrow text-ink text-xs">{`0${i + 1}`} / Service</p>
                  </div>
                  <h2 className="heading mt-5 text-3xl md:text-4xl text-ink">{s.title}</h2>
                  <p className="mt-3 text-ink/90 text-lg">{s.tagline}</p>
                  <p className="mt-3 text-ink/85">{s.description}</p>
                </div>
                <div className={`md:col-span-5 card-brut bg-cream p-8 flex flex-col justify-between ${i % 2 ? 'md:order-1' : ''}`}>
                  <div>
                    <p className="eyebrow text-xs text-ink/70">Includes</p>
                    <ul className="mt-4 space-y-3">
                      {(s.inclusions || []).map((x) => (
                        <li key={x} className="flex items-start gap-2"><SparkleIcon className="h-4 w-4 text-coral mt-1 shrink-0" /> <span>{x}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {detail && (
                      <button
                        type="button"
                        onClick={() => setDetailKey(s.id)}
                        className="btn-brut"
                        data-testid={`service-view-prices-${s.id}`}
                      >
                        View prices & details <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                    <Link to="/contact#enquiry-form" className="btn-brut ghost" data-testid={`service-enquire-${s.id}`}>
                      Enquire <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="grain-pink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-5xl px-5 py-20 md:py-24 md:px-10 text-center">
          <div className="flex justify-center gap-6 text-ink mb-4"><LipstickIcon /><CombIcon /></div>
          <h2 className="heading text-3xl md:text-5xl text-ink">{sp.final_headline || "Got something else in mind?"}</h2>
          <p className="mt-4 text-lg text-ink/90">{sp.final_paragraph || "If it has a face and a moment, I can probably help. Send a brief and we’ll build the day around it."}</p>
          <Link to="/contact#enquiry-form" className="btn-brut mt-6" data-testid="services-final-cta">{sp.final_cta || "Tell me about it"} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <ServiceDetailModal
        open={!!detailKey}
        detail={detailKey ? detailByGroup[detailKey] : null}
        onClose={() => setDetailKey(null)}
      />
    </div>
  );
}
